/**
 * `useRemoveAssetApprovalChain` mutation hook.
 *
 * Wraps the raw `removeAssetApprovalChain` service fn (per AD-23). Invalidations
 * mirror `useAssignAssetApprovalChain` — the asset's approval chain query, its file
 * detail (removing a chain the asset completed reopens it), the project's approval chain
 * queries (`assetUsageCount` drops), the folder files query, and the folder list/detail
 * aggregates so the progress bar recomputes.
 *
 * Side effects: NONE beyond invalidation + analytics; caller owns toast/confirm.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ASSET_APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/asset-approval-chain';
import { PROJECT_FILE_QUERY_KEYS } from '@/entities/project-file';
import { PROJECT_FOLDER_QUERY_KEYS } from '@/entities/project-folder';
import { APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/approval-chain';
import { track } from '@/shared/analytics';

import { removeAssetApprovalChain } from './asset-approval-chain.mutations.api';
import { invalidateFolderAggregates } from './invalidate-folder-aggregates';

export { removeAssetApprovalChain };

interface UseRemoveAssetApprovalChainOptions {
  projectId: string;
  /** Folder currently in view — used to refresh its files list after removing. */
  folderId?: string;
}

export function useRemoveAssetApprovalChain(options: UseRemoveAssetApprovalChainOptions) {
  const queryClient = useQueryClient();
  const { projectId, folderId } = options;

  return useMutation({
    mutationFn: (assetId: string) => removeAssetApprovalChain(projectId, assetId),
    onSuccess: (_data, assetId) => {
      void queryClient.invalidateQueries({
        queryKey: ASSET_APPROVAL_CHAIN_QUERY_KEYS.detail(projectId, assetId),
      });
      void queryClient.invalidateQueries({
        queryKey: PROJECT_FILE_QUERY_KEYS.detail(projectId, assetId),
      });
      void queryClient.invalidateQueries({
        queryKey: APPROVAL_CHAIN_QUERY_KEYS.all(projectId),
      });
      if (folderId) {
        void queryClient.invalidateQueries({
          queryKey: PROJECT_FOLDER_QUERY_KEYS.files(projectId, folderId),
        });
      }
      invalidateFolderAggregates(queryClient, projectId, folderId);
      track('asset_workflow_removed', { project_id: projectId });
    },
  });
}
